// GoalsPage — Set and track trading/savings goals
// Layout:
//   - Top nav: < Back | "Goals"
//   - Hero: current progress
//   - Active goals (cards with progress bar)
//   - Achieved goals
//   - "Create new goal" CTA

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardList, TfSectionHeaderView, TfBadge, TfProgress } from '../../components/TfPrimitives';

interface Goal {
  id: string;
  title: string;
  description: string;
  type: 'pnl' | 'streak' | 'trades' | 'challenge';
  target: number;
  current: number;
  unit: string;
  deadline: string;
  icon: string;
  color: string;
  achieved?: boolean;
}

const GOALS: Goal[] = [
  { id: 'g1', title: 'Hit $5,000 monthly profit',     description: 'Reach $5K net profit this month',       type: 'pnl',     target: 5000, current: 2920, unit: '$',   deadline: '2026-07-31', icon: '💰', color: '#34C759' },
  { id: 'g2', title: 'Maintain 65% win rate',         description: 'Keep win rate above 65%',               type: 'pnl',     target: 65,   current: 67,   unit: '%',   deadline: '2026-08-31', icon: '🎯', color: '#007AFF' },
  { id: 'g3', title: '10-trade winning streak',       description: 'Hit 10 winning trades in a row',         type: 'streak',  target: 10,   current: 5,    unit: ' trades', deadline: '2026-08-15', icon: '🔥', color: '#FF9500' },
  { id: 'g4', title: '100 trades this month',         description: 'Place 100 trades before August 1',      type: 'trades',  target: 100,  current: 87,   unit: ' trades', deadline: '2026-08-01', icon: '📈', color: '#5856D6' },
  { id: 'g5', title: 'Pass first challenge',          description: 'Get funded on your first challenge',     type: 'challenge', target: 1,  current: 0.4,  unit: '',      deadline: '2026-09-30', icon: '💎', color: '#AF52DE' },
];

const ACHIEVED: Goal[] = [
  { id: 'a1', title: 'First challenge activated', description: 'Activated first challenge',     type: 'challenge', target: 1, current: 1, unit: '', deadline: '2026-07-04', icon: '🎯', color: '#34C759', achieved: true },
  { id: 'a2', title: '5 winning trades in a row', description: 'Hit a 5-trade win streak',      type: 'streak',    target: 5, current: 5, unit: '', deadline: '2026-07-22', icon: '🔥', color: '#FF9500', achieved: true },
];

const NEW_GOAL_TEMPLATES = [
  { title: 'Hit $10K monthly profit', type: 'pnl', target: 10000, unit: '$',     icon: '💰' },
  { title: '20-trade winning streak', type: 'streak', target: 20, unit: ' trades', icon: '🔥' },
  { title: 'Pass 3 challenges',      type: 'challenge', target: 3, unit: '',   icon: '💎' },
  { title: 'Daily trading streak',   type: 'streak', target: 30, unit: ' days',  icon: '📅' },
  { title: 'Sharpe > 2.0',           type: 'pnl', target: 2, unit: '',        icon: '📊' },
];

export function GoalsPage() {
  const nav = useNavigate();
  const [creating, setCreating] = useState(false);
  const active = GOALS.filter((g) => !g.achieved);
  const completed = ACHIEVED;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">Goals</div>
        <button onClick={() => setCreating(true)} data-testid="new-goal" className="px-3 text-[15px] font-semibold text-tf-blue">
          New
        </button>
      </div>

      {/* Hero — current progress */}
      <div className="mx-4 mt-4">
        <TfCard>
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3 text-center">Active goals</div>
          <div className="mt-1 text-[40px] font-bold text-tf-text-1 tabular-nums text-center leading-none">{active.length}</div>
          <div className="mt-1 text-[13px] text-tf-text-3 text-center">{completed.length} achieved all time</div>
        </TfCard>
      </div>

      {/* Active goals list */}
      <TfSectionHeaderView title={`In progress (${active.length})`} viewAllLabel="Filter" onViewAll={() => alert('Filter goals')} />
      <div className="mx-4 space-y-2">
        {active.map((g) => <GoalCard key={g.id} goal={g} />)}
      </div>

      {/* Achieved */}
      <TfSectionHeaderView title="Achieved" />
      <div className="mx-4">
        <TfCardList>
          {completed.map((g, i) => (
            <div
              key={g.id}
              data-testid={`achieved-${g.id}`}
              className={['flex items-center gap-3 px-4 py-3', i < completed.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-tf-profit/15 text-[20px]">
                {g.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-tf-text-1">{g.title}</div>
                <div className="text-[11px] text-tf-text-3">Achieved {g.deadline}</div>
              </div>
              <span className="text-[20px]">🏆</span>
            </div>
          ))}
        </TfCardList>
      </div>

      {/* New goal modal */}
      {creating && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm" onClick={() => setCreating(false)}>
          <div
            className="w-full max-w-[420px] rounded-t-[20px] p-4 pb-6 text-tf-text-1"
            style={{ background: 'var(--tf-bg)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-tf-border" />
            <h2 className="px-1 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">New goal</h2>
            <p className="px-1 pb-3 text-[13px] text-tf-text-3">Pick a template to get started</p>
            <div className="space-y-1.5">
              {NEW_GOAL_TEMPLATES.map((t) => (
                <button
                  key={t.title}
                  onClick={() => { alert('Goal created: ' + t.title); setCreating(false); }}
                  className="flex w-full items-center gap-3 rounded-[12px] px-3 py-2.5 active:bg-tf-surface-1 transition"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue/10 text-[18px]">{t.icon}</div>
                  <div className="flex-1 text-left">
                    <div className="text-[14px] font-semibold text-tf-text-1">{t.title}</div>
                    <div className="text-[11px] text-tf-text-3">{t.target.toLocaleString()}{t.unit}</div>
                  </div>
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setCreating(false)} className="h-12 flex-1 rounded-full bg-tf-surface-1 text-[15px] font-semibold text-tf-text-1 active:opacity-40">Cancel</button>
              <button onClick={() => setCreating(false)} className="h-12 flex-1 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.99]">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GoalCard({ goal }: { goal: Goal }) {
  const pct = Math.min(100, (goal.current / goal.target) * 100);
  const daysLeft = Math.max(0, Math.ceil((new Date(goal.deadline).getTime() - Date.now()) / 86400000));
  return (
    <TfCard className="p-3" testId={`goal-${goal.id}`}>
      <div className="flex items-start gap-3">
        <div
          className="flex h-12 w-12 items-center justify-center rounded-[14px] text-[24px]"
          style={{ backgroundColor: goal.color + '18' }}
        >
          {goal.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="text-[15px] font-bold text-tf-text-1">{goal.title}</h3>
            <TfBadge variant="info">{daysLeft}d left</TfBadge>
          </div>
          <p className="text-[12px] text-tf-text-3 mt-0.5">{goal.description}</p>
          <div className="mt-2 flex items-center justify-between">
            <div className="text-[13px] font-semibold tabular-nums" style={{ color: goal.color }}>
              {goal.unit === '$' ? '$' : ''}{goal.current.toLocaleString()}{goal.unit !== '$' ? goal.unit : ''}
              <span className="text-tf-text-3 font-normal"> / {goal.unit === '$' ? '$' : ''}{goal.target.toLocaleString()}{goal.unit !== '$' ? goal.unit : ''}</span>
            </div>
            <div className="text-[12px] font-bold tabular-nums" style={{ color: goal.color }}>{pct.toFixed(0)}%</div>
          </div>
          <div className="mt-1.5">
            <TfProgress value={pct} color={goal.color} />
          </div>
        </div>
      </div>
    </TfCard>
  );
}
